import { useEffect } from "react";

// Zarządzanie <head> dla stron bloga. Nadpisuje globalne tagi z public/index.html
// (zamiast je duplikować — dwa canonicale albo dwa description to błąd SEO),
// a przy odmontowaniu przywraca poprzedni stan.
//
// prerender.js robi snapshot DOM-u po odpaleniu efektów, więc wszystko poniżej
// ląduje w statycznym HTML-u i jest widoczne bez JS.

function apply(el, attrs) {
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") el.removeAttribute(k);
    else el.setAttribute(k, String(v));
  });
}

function upsert(undo, selector, factory, attrs) {
  const existing = document.head.querySelector(selector);
  if (existing) {
    const prev = Object.keys(attrs).reduce((acc, k) => {
      acc[k] = existing.getAttribute(k);
      return acc;
    }, {});
    apply(existing, attrs);
    undo.push(() => apply(existing, prev));
    return existing;
  }
  const el = factory();
  apply(el, attrs);
  document.head.appendChild(el);
  undo.push(() => el.remove());
  return el;
}

const metaName = (name) => () => {
  const m = document.createElement("meta");
  m.setAttribute("name", name);
  return m;
};
const metaProp = (property) => () => {
  const m = document.createElement("meta");
  m.setAttribute("property", property);
  return m;
};
const linkRel = (rel) => () => {
  const l = document.createElement("link");
  l.setAttribute("rel", rel);
  return l;
};

/**
 * @param {object} spec
 * @param {string} spec.title            <title> — dokładnie to, co w frontmatter
 * @param {string} spec.description      meta description
 * @param {string} spec.canonical        absolutny URL kanoniczny
 * @param {string} spec.lang             język strony (atrybut lang na <html>)
 * @param {string} [spec.locale]         og:locale
 * @param {string} [spec.robots]         np. "noindex, follow"
 * @param {"website"|"article"} [spec.ogType]
 * @param {string} [spec.image]          absolutny URL obrazka OG
 * @param {Array}  [spec.alternates]     [{hrefLang, href}]
 * @param {string} [spec.rss]            absolutny URL kanału RSS
 * @param {string} [spec.prev]           absolutny URL poprzedniej strony paginacji
 * @param {string} [spec.next]           absolutny URL następnej strony paginacji
 * @param {object} [spec.article]        {publishedTime, modifiedTime, author, tags}
 * @param {Array}  [spec.jsonLd]         obiekty JSON-LD
 */
export default function useBlogHead(spec) {
  const key = JSON.stringify(spec);

  useEffect(() => {
    const undo = [];
    const prevTitle = document.title;
    document.title = spec.title;
    undo.push(() => {
      document.title = prevTitle;
    });

    const prevLang = document.documentElement.getAttribute("lang");
    document.documentElement.setAttribute("lang", spec.lang);
    undo.push(() => document.documentElement.setAttribute("lang", prevLang || "en"));

    upsert(undo, 'meta[name="title"]', metaName("title"), { content: spec.title });
    upsert(undo, 'meta[name="description"]', metaName("description"), {
      content: spec.description,
    });
    if (spec.robots) {
      upsert(undo, 'meta[name="robots"]', metaName("robots"), { content: spec.robots });
    }
    upsert(undo, 'link[rel="canonical"]', linkRel("canonical"), { href: spec.canonical });

    upsert(undo, 'meta[property="og:type"]', metaProp("og:type"), {
      content: spec.ogType || "website",
    });
    upsert(undo, 'meta[property="og:title"]', metaProp("og:title"), { content: spec.title });
    upsert(undo, 'meta[property="og:description"]', metaProp("og:description"), {
      content: spec.description,
    });
    upsert(undo, 'meta[property="og:url"]', metaProp("og:url"), { content: spec.canonical });
    if (spec.image) {
      upsert(undo, 'meta[property="og:image"]', metaProp("og:image"), { content: spec.image });
    }
    if (spec.locale) {
      upsert(undo, 'meta[property="og:locale"]', metaProp("og:locale"), { content: spec.locale });
    }

    // Twitter Card — istniejące tagi na stronie używają property=, trzymamy się tego
    upsert(undo, 'meta[property="twitter:card"]', metaProp("twitter:card"), {
      content: "summary_large_image",
    });
    upsert(undo, 'meta[property="twitter:title"]', metaProp("twitter:title"), {
      content: spec.title,
    });
    upsert(undo, 'meta[property="twitter:description"]', metaProp("twitter:description"), {
      content: spec.description,
    });
    upsert(undo, 'meta[property="twitter:url"]', metaProp("twitter:url"), {
      content: spec.canonical,
    });
    if (spec.image) {
      upsert(undo, 'meta[property="twitter:image"]', metaProp("twitter:image"), {
        content: spec.image,
      });
    }

    if (spec.article) {
      const a = spec.article;
      const created = [];
      const add = (property, content) => {
        if (!content) return;
        const m = document.createElement("meta");
        m.setAttribute("property", property);
        m.setAttribute("content", content);
        document.head.appendChild(m);
        created.push(m);
      };
      add("article:published_time", a.publishedTime);
      add("article:modified_time", a.modifiedTime);
      add("article:author", a.author);
      (a.tags || []).forEach((tag) => add("article:tag", tag));
      undo.push(() => created.forEach((m) => m.remove()));
    }

    const links = [];
    (spec.alternates || []).forEach((alt) => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", alt.hrefLang);
      l.setAttribute("href", alt.href);
      document.head.appendChild(l);
      links.push(l);
    });
    if (spec.rss) {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("type", "application/rss+xml");
      l.setAttribute("title", spec.title);
      l.setAttribute("href", spec.rss);
      document.head.appendChild(l);
      links.push(l);
    }
    [
      ["prev", spec.prev],
      ["next", spec.next],
    ].forEach(([rel, href]) => {
      if (!href) return;
      const l = document.createElement("link");
      l.setAttribute("rel", rel);
      l.setAttribute("href", href);
      document.head.appendChild(l);
      links.push(l);
    });
    undo.push(() => links.forEach((l) => l.remove()));

    const scripts = (spec.jsonLd || []).map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-blog-ld", "");
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });
    undo.push(() => scripts.forEach((s) => s.remove()));

    return () => undo.reverse().forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
